import React, { Fragment, useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../overmind';
import { KeplerState } from '../../models/kepler-state';
import { useForm } from 'react-hook-form';
import { ProfileEmailBlock } from './profile-email-block/profile-email-block';
import { FetchStatus, FetchType } from '../../enums';
import { PagePath } from '../../navigation/navigation.enums';
import { PageLoaderLayer } from '../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { getSubdomain } from '../../library/helpers/get-subdomain';
import { Anchor, AvatarIcon, Button, DropdownSelect, DropdownSelectOption, FieldController, Modal, NameValidator, RequiredValidator, TextField } from '@keplerco/core';
import { PeriodOfExperience, PeriodOfExperienceDescription } from '../../enums/period-of-experience';
import { Profile } from '../../models/profile';
import styles from './profile.module.css';

type EditModeType = 'none' | 'personal' | 'contact';

export function ProfilePage(): JSX.Element {
  const isCPD = getSubdomain().includes('cpd');

  const actions = useAppActions();
  const { user, fetchState, companyVariables } = useAppState<KeplerState>();

  const { control, setValue, handleSubmit } = useForm<any>({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const [profile, setProfile] = useState<Profile>();
  const [editMode, setEditMode] = useState<EditModeType>('none');
  const [submittedResetPassword, setSubmittedResetPassword] = useState<boolean>(false);
  const [options, setOptions] = useState<DropdownSelectOption[]>([
    { label: PeriodOfExperienceDescription.ZeroToTwo, value: PeriodOfExperience.ZeroToTwo.toString(), onClick: () => onClickPeriodOfExperience(PeriodOfExperience.ZeroToTwo) },
    { label: PeriodOfExperienceDescription.TwoToFour, value: PeriodOfExperience.TwoToFour.toString(), onClick: () => onClickPeriodOfExperience(PeriodOfExperience.TwoToFour) },
    { label: PeriodOfExperienceDescription.FourToSeven, value: PeriodOfExperience.FourToSeven.toString(), onClick: () => onClickPeriodOfExperience(PeriodOfExperience.FourToSeven) },
    { label: PeriodOfExperienceDescription.SevenPlus, value: PeriodOfExperience.SevenPlus.toString(), onClick: () => onClickPeriodOfExperience(PeriodOfExperience.SevenPlus) },
  ]);

  function onClickPeriodOfExperience(periodOfExperience: PeriodOfExperience) {
    setOptions(currentState => currentState.map(option => ({ ...option, selected: parseInt(option.value) === periodOfExperience })));
  }

  useEffect(() => {
    async function getData() {
      actions.startLoader({ path: PagePath.profile, type: FetchType.PageFetching });

      const profileResponse = await actions.getProfile();
      setProfile(profileResponse);

      actions.stopLoader(PagePath.profile);
    }

    if (!profile) getData();
  }, [companyVariables.slug]);

  useEffect(() => {
    if (!profile) return;
    setValue('firstName', profile.firstName);
    setValue('lastName', profile.lastName);
    setValue('preferredName', profile.preferredName);
    setValue('jobTitle', profile.jobTitle);
    setValue('periodOfExperience', profile.periodOfExperience.toString());

    setOptions(currentState => currentState.map(option => ({ ...option, selected: parseInt(option.value) === profile.periodOfExperience })));
  }, [profile]);

  function toggleEditMode(mode: EditModeType): void {
    setEditMode(editMode === 'none' ? mode : 'none');
  }

  return (
    <PageLoaderLayer path={PagePath.profile}>
      <div className="wrapper pageWrapper" style={{ paddingBottom: 30 }}>
        <header className={styles.profileHeader}>
          <div className={styles.profileHeaderIcon}>
            <AvatarIcon name={{ firstName: user?.firstName, lastName: user?.lastName }} />
          </div>

          <div>
            <h1>{profile?.firstName} {profile?.lastName}</h1>
            <h3 style={{ color: 'var(--accent-2)' }}>{profile?.jobTitle}</h3>
          </div>
        </header>

        <section className={styles.profileBody}>
          <div className="card" style={{ gridArea: 'about' }}>
            <div className={styles.profileCardHeader}>
              <h3 className={styles.profileCardHeaderTitle}>About</h3>

              {editMode !== 'personal' && <Anchor onClick={() => toggleEditMode('personal')}>Edit Details</Anchor>}
            </div>

            <form
              id="saveProfile"
              onSubmit={handleSubmit(async fieldValues => {
                actions.startLoader({ path: PagePath.profile, type: FetchType.Sending });
                const request: Profile = {
                  ...profile,
                  ...fieldValues,
                  periodOfExperience: parseInt(fieldValues.periodOfExperience),
                };
                const response = await actions.saveProfile(request);
                setProfile(response);
                setEditMode('none');
                actions.stopLoader(PagePath.profile);
              })}
            >
              {editMode === 'personal' ? (
                <FieldController
                  control={control}
                  name="firstName"
                  rules={new NameValidator('Enter your first name')}
                  render={({ field, fieldState }) => {
                    return (
                      <TextField
                        {...field}
                        type="text"
                        label="First name"
                        responsive
                        validation={{
                          dirty: fieldState.isDirty || !!fieldState.error,
                          invalid: !!fieldState.error,
                          message: fieldState.error?.message ?? 'Enter your first name',
                        }}
                      />
                    );
                  }}
                />
              ) : (
                <div className={styles.profileCardAboutItem}>
                  <small style={{ color: 'var(--accent-3)' }}>First name</small>
                  <div>{profile?.firstName}</div>
                </div>
              )}

              {editMode === 'personal' ? (
                <FieldController
                  control={control}
                  name="lastName"
                  rules={new NameValidator('Enter your last name')}
                  render={({ field, fieldState }) => {
                    return (
                      <TextField
                        {...field}
                        type="text"
                        label="Last name"
                        responsive
                        validation={{
                          dirty: fieldState.isDirty || !!fieldState.error,
                          invalid: !!fieldState.error,
                          message: fieldState.error?.message ?? 'Enter your last name',
                        }}
                      />
                    );
                  }}
                />
              ) : (
                <div className={styles.profileCardAboutItem}>
                  <small style={{ color: 'var(--accent-3)' }}>Last name</small>
                  <div>{profile?.lastName}</div>
                </div>
              )}

              {editMode === 'personal' ? (
                <FieldController
                  control={control}
                  name="preferredName"
                  rules={new NameValidator('Enter your preferred name')}
                  render={({ field, fieldState }) => {
                    return (
                      <TextField
                        {...field}
                        type="text"
                        label="Preferred name"
                        responsive
                        validation={{
                          dirty: fieldState.isDirty || !!fieldState.error,
                          invalid: !!fieldState.error,
                          message: fieldState.error?.message ?? 'Enter your preferred name',
                        }}
                      />
                    );
                  }}
                />
              ) : (
                <div className={styles.profileCardAboutItem}>
                  <small style={{ color: 'var(--accent-3)' }}>Preferred name</small>
                  <div>{profile?.preferredName}</div>
                </div>
              )}

              {editMode === 'personal' ? (
                <FieldController
                  control={control}
                  name="jobTitle"
                  rules={new RequiredValidator('Enter your job title')}
                  render={({ field, fieldState }) => {
                    return (
                      <TextField
                        {...field}
                        type="text"
                        label="Job title"
                        responsive
                        validation={{
                          dirty: fieldState.isDirty || !!fieldState.error,
                          invalid: !!fieldState.error,
                          message: fieldState.error?.message ?? 'Enter your job title',
                        }}
                      />
                    );
                  }}
                />
              ) : (
                <div className={styles.profileCardAboutItem}>
                  <small style={{ color: 'var(--accent-3)' }}>Job title</small>
                  <div>{profile?.jobTitle}</div>
                </div>
              )}

              {editMode === 'personal' ? (
                <FieldController
                  control={control}
                  name="periodOfExperience"
                  rules={new RequiredValidator('Select years of experience')}
                  render={({ field, fieldState }) => (
                    <DropdownSelect
                      {...field}
                      label="Years of experience"
                      options={options}
                      responsive
                      validation={{
                        dirty: fieldState.isDirty || !!fieldState.error,
                        invalid: fieldState.invalid,
                        message: fieldState.error?.message ?? 'Select years of experience',
                      }}
                    />
                  )}
                />
              ) : (
                <Fragment>
                  {profile?.periodOfExperience !== undefined && (
                    <div className={styles.profileCardAboutItem}>
                      <small style={{ color: 'var(--accent-3)' }}>Years of experience</small>
                      <div>{options.find(option => parseInt(option.value) === profile.periodOfExperience)?.label}</div>
                    </div>
                  )}
                </Fragment>
              )}

              {editMode === 'personal' && (
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                  <Button type="button" filled square>
                    Save Details
                  </Button>
                </div>
              )}
            </form>
          </div>

          {!!profile && (
            <div className="card" style={{ gridArea: 'contact' }}>
              <div className={styles.profileCardHeader}>
                <h3 className={styles.profileCardHeaderTitle}>Contact</h3>
              </div>

              <div className={styles.profileCardAboutItem}>
                <ProfileEmailBlock email={profile.email} isPrimary={true} hideChip={isCPD} verificationDate={new Date()} />

                {profile.emails?.map(userEmail => (
                  <ProfileEmailBlock
                    key={userEmail.id}
                    email={userEmail.email}
                    isPrimary={false}
                    hideChip={isCPD}
                    verificationDate={userEmail.dateConfirmed}
                    onResendVerification={async () => {
                      actions.startLoader({ path: PagePath.profile, type: FetchType.Sending });
                      await actions.resendVerification(userEmail.email);
                      setProfile(await actions.getProfile());
                      actions.stopLoader(PagePath.profile);
                    }}
                    onMakePrimary={async () => {
                      actions.startLoader({ path: PagePath.profile, type: FetchType.Sending });
                      await actions.changePrimaryEmail(userEmail.email);
                      setProfile(await actions.getProfile());
                      actions.stopLoader(PagePath.profile);
                    }}
                    onDelete={async () => {
                      actions.startLoader({ path: PagePath.profile, type: FetchType.Sending });
                      await actions.removeEmail(userEmail);
                      setProfile(await actions.getProfile());
                      actions.stopLoader(PagePath.profile);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="card" style={{ gridArea: 'security' }}>
            <h3 className={styles.profileCardHeaderTitle}>Security</h3>

            <div style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between' }}>
              <div>
                <span>Password</span>
                <div className="subtitle">********************</div>
              </div>

              <Anchor
                color="link-text"
                onClick={async () => {
                  if (profile?.email !== undefined && fetchState[PagePath.profile].status === FetchStatus.Inactive) {
                    actions.startLoader({ path: PagePath.profile, type: FetchType.Sending });
                    await actions.forgotPassword({ email: profile.email });
                    setSubmittedResetPassword(true);
                    actions.stopLoader(PagePath.profile);
                  }
                }}
              >
                Change Password
              </Anchor>
            </div>
          </div>
        </section>
      </div>

      <Modal open={submittedResetPassword} onClose={() => setSubmittedResetPassword(false)}>
        <div className="modalContent" style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
          <h2>We've emailed a password reset link to you</h2>

          <h3>Please follow the link to set a new password.</h3>

          <footer style={{ textAlign: 'center' }}>
            <Button filled type="button" onClick={() => setSubmittedResetPassword(false)}>
              GOT IT
            </Button>
          </footer>
        </div>
      </Modal>
    </PageLoaderLayer>
  );
}
